import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions } from '../../../../../adapters/types'
import { DischargeFormData } from '../../../../types/formData'
import { DischargeFormField } from '../../../../types/formField'
import { DischargeAssessmentQuestionnaireItem } from '../../../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../../../helpers'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { FlatAnswerMutation } from '../../../../../adapters/types'

/**
 * Integumentary subsection adapter
 * Handles skin assessment, wounds, and nail condition (6 fields)
 */
export class IntegumentaryAdapter extends BaseSubsectionAdapter<DischargeFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<DischargeFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Integumentary system assessment fields
    const skinColor = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_SKIN_COLOR
    )?.at(0)

    const skinIssues = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_SKIN_ISSUES
    )

    const nails = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_NAILS
    )?.at(0)

    const identifiedWounds = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_IDENTIFIED_WOUNDS
    )?.at(0)

    const woundNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_WOUND_NOTES
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [DischargeFormField.INTEGUMENTARY_SKIN_COLOR]: skinColor,
      [DischargeFormField.INTEGUMENTARY_SKIN_ISSUES]: skinIssues,
      [DischargeFormField.INTEGUMENTARY_NAILS]: nails,
      [DischargeFormField.INTEGUMENTARY_IDENTIFIED_WOUNDS]: identifiedWounds,
      [DischargeFormField.INTEGUMENTARY_WOUND_NOTES]: woundNotes,
      [DischargeFormField.INTEGUMENTARY_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(DischargeFormField.INTEGUMENTARY_SKIN_COLOR)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_SKIN_COLOR,
        value: this.getFormDataField(DischargeFormField.INTEGUMENTARY_SKIN_COLOR)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.INTEGUMENTARY_SKIN_ISSUES)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_SKIN_ISSUES,
        value: this.getFormDataField(DischargeFormField.INTEGUMENTARY_SKIN_ISSUES)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.INTEGUMENTARY_NAILS)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_NAILS,
        value: this.getFormDataField(DischargeFormField.INTEGUMENTARY_NAILS)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.INTEGUMENTARY_IDENTIFIED_WOUNDS)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_IDENTIFIED_WOUNDS,
        value: this.getFormDataField(DischargeFormField.INTEGUMENTARY_IDENTIFIED_WOUNDS)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.INTEGUMENTARY_WOUND_NOTES)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_WOUND_NOTES,
        value: this.getFormDataField(DischargeFormField.INTEGUMENTARY_WOUND_NOTES)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.INTEGUMENTARY_SYSTEM_SUMMARY)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_SYSTEM_SUMMARY,
        value: this.getFormDataField(DischargeFormField.INTEGUMENTARY_SYSTEM_SUMMARY)
      })
    }

    return mutations
  }
}
